import { ElementBase } from "@smartdesign/element-base";

const htmlTemplate = require("!raw-loader!./button.html").default;

declare global {
    interface HTMLElementTagNameMap {
        [Button.ID]: Button;
    }
}

export class Button extends ElementBase {
    public static readonly ID = "sd-button";
    private _image: HTMLImageElement;
    private _isSpaceOrEnterKeyDown = false;

    public get imageElement() {
        return this._image;
    }

    public get icon(): string {
        return this.getAttribute("icon");
    }

    public set icon(icon: string) {
        if (this.icon !== icon) {
            if (icon) {
                this.setAttribute("icon", icon);
            } else {
                this.removeAttribute("icon");
            }
            this.updateIcon();
        }
    }

    public get primary(): boolean {
        return this.hasAttribute("primary");
    }

    public set primary(primary: boolean) {
        if (primary) {
            this.setAttribute("primary", "");
        } else {
            this.removeAttribute("primary");
        }
    }

    public get disabled(): boolean {
        return this.getAttribute("aria-disabled") == "true";
    }

    public set disabled(disabled: boolean) {
        this.setAttribute("aria-disabled", disabled.toString());
    }

    static get observedAttributes() {
        return ["icon"];
    }

    public is(): string {
        return Button.ID;
    }

    protected template(): HTMLTemplateElement {
        const template = document.createElement("template");
        template.innerHTML = htmlTemplate;
        return template;
    }

    constructor() {
        super();
        this.addEventListener("keydown", (e) => {
            if (this.isSpaceOrEnter(e)) {
                this._isSpaceOrEnterKeyDown = true;
                e.preventDefault(); // prevent potential scroll-down triggered by space
            }
        });
        this.addEventListener("keyup", (e) => {
            if (this.isSpaceOrEnter(e) && this._isSpaceOrEnterKeyDown == true) {
                this._isSpaceOrEnterKeyDown = false;
                this.click();
            }
        });
        // As Button is not an input element, click is fired even in disabled state
        this.addEventListener(
            "click",
            (event) => {
                if (this.disabled || this.hasAttribute("disabled")) {
                    event.stopImmediatePropagation();
                    return;
                }

                const type = this.getAttribute("type")?.toLowerCase()?.trim();

                if (type !== "submit" && type !== "reset") {
                    return;
                }

                const form = this.closest("form");

                if (form && type === "submit") {
                    form.requestSubmit();
                }

                if (form && type === "reset") {
                    form.reset();
                }
            },
            { capture: true }
        );
    }

    private isSpaceOrEnter(event: KeyboardEvent): boolean {
        switch (event.key) {
            case " ":
            case "Space":
            case "Enter":
                return true;
            default:
                return false;
        }
    }

    public click() {
        if (!this.disabled && !this.hasAttribute("disabled")) {
            super.click();
        }
    }

    public connectedCallback() {
        super.connectedCallback();

        if (!this.hasAttribute("tabIndex")) {
            this.tabIndex = 0;
        }

        this._image = this.shadowRoot.querySelector(".icon") as HTMLImageElement;

        this.updateIcon();
        if (!this.hasAttribute("role")) {
            this.setAttribute("role", "button");
        }
    }

    public attributeChangedCallback(): void {
        this.updateIcon();
    }

    private updateIcon() {
        if (this._image) {
            const iconSrc = this.getAttribute("icon");
            if (iconSrc) {
                this._image.src = iconSrc;
            }
        }
    }
}

if (!customElements.get(Button.ID)) {
    customElements.define(Button.ID, Button);
}
